import React, { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Upload, FolderOpen, Copy, AlertCircle, CheckCircle2, File as FileIcon, X
} from 'lucide-react';
import { uploadFile, UploadResponse } from '../api/files';
import PageHeader from '../components/shared/PageHeader';

interface RecentUpload {
  id: string;
  fileName: string;
  size: number;
  uploadedAt: number;
  url: string;
  status: 'done' | 'failed';
}

const formatSize = (bytes: number) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const units = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${units[i]}`;
};

const formatAge = (ts: number) => {
  const diff = Date.now() - ts;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const m = Math.floor(sec / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
};

const FileUploadPage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [desiredName, setDesiredName] = useState('');
  const [recent, setRecent] = useState<RecentUpload[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const mutation = useMutation({
    mutationFn: ({ file, name }: { file: File; name?: string }) => uploadFile(file, name),
    onSuccess: (data: UploadResponse) => {
      setError(null);
      // Backend returns a record like { fileName, url } or { ...formats }; pick a readable label.
      const entries = Object.entries(data);
      const fileEntry = entries.find(([k]) => /name|file|key/i.test(k)) ?? entries[0];
      const fileName = (fileEntry?.[1] as string) ?? file?.name ?? 'upload';
      const url = (data as { url?: string }).url ?? fileName;
      setRecent(prev => [
        {
          id: `${Date.now()}-${fileName}`,
          fileName,
          size: file?.size ?? 0,
          uploadedAt: Date.now(),
          url,
          status: 'done'
        },
        ...prev.slice(0, 9)
      ]);
      setFile(null);
      setDesiredName('');
    },
    onError: () => setError('Upload failed. Try again.')
  });

  const handleFile = (f: File) => {
    setFile(f);
    setError(null);
    if (!desiredName) setDesiredName(f.name.replace(/\.[^/.]+$/, ''));
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  return (
    <>
      <PageHeader
        title="Files"
        subtitle="Upload images, videos, and other assets to MinIO storage."
        actions={
          <button className="hf-btn"><FolderOpen size={14} /> Browse storage</button>
        }
      />

      <div className="hf-content">
        <div className="hf-two-col wide-right">
          {/* Left — dropzone + naming */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
              className="hf-feature-box"
              style={{
                padding: 28,
                background: dragActive ? 'var(--accent-soft)' : 'var(--surface-2)',
                borderStyle: 'dashed',
                borderColor: dragActive ? 'var(--accent)' : 'var(--border-2)',
                borderWidth: 2,
                minHeight: 240,
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                cursor: 'pointer'
              }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
            >
              <Upload size={36} color="var(--fg-3)" />
              <div style={{ fontSize: 16, fontWeight: 600 }}>Drop files here</div>
              <div className="hf-help" style={{ marginTop: 0 }}>
                or{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); e.stopPropagation(); inputRef.current?.click(); }} style={{ color: 'var(--accent)' }}>browse</a>
                {' '}— images, videos, archives
              </div>
              <input
                ref={inputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
              />
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <span className="hf-tag outline sm">paste from clipboard</span>
                <span className="hf-tag outline sm">paste URL</span>
              </div>
            </div>

            {file && (
              <div className="hf-card">
                <div className="hf-card-header">
                  <h3 className="hf-card-title">Ready to upload</h3>
                  <button className="hf-btn ghost sm" onClick={() => setFile(null)}><X size={12} /></button>
                </div>
                <div className="hf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <FileIcon size={20} color="var(--fg-2)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</div>
                      <div className="hf-help" style={{ marginTop: 0 }}>{formatSize(file.size)} · {file.type || 'unknown'}</div>
                    </div>
                  </div>

                  <div>
                    <div className="hf-label-row"><span className="hf-label">Desired name (optional)</span></div>
                    <div className="hf-input">
                      <input
                        type="text"
                        value={desiredName}
                        onChange={(e) => setDesiredName(e.target.value)}
                        placeholder="filename-without-extension"
                      />
                      <span className="hf-mono" style={{ color: 'var(--fg-3)' }}>.{file.name.split('.').pop()}</span>
                    </div>
                    <div className="hf-help">Original extension is kept.</div>
                  </div>

                  <button
                    className="hf-btn primary"
                    onClick={() => mutation.mutate({ file, name: desiredName || undefined })}
                    disabled={mutation.isPending}
                  >
                    <Upload size={14} /> {mutation.isPending ? 'Uploading…' : 'Upload'}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div className="hf-banner danger">
                <AlertCircle size={14} /> {error}
                <button className="hf-btn ghost sm" style={{ marginLeft: 'auto' }} onClick={() => setError(null)}><X size={12} /></button>
              </div>
            )}
          </div>

          {/* Right — recent uploads */}
          <div className="hf-card">
            <div className="hf-card-header">
              <h3 className="hf-card-title">Recent uploads</h3>
              <span className="hf-count">{recent.length}</span>
            </div>
            <div className="hf-card-body" style={{ padding: 0 }}>
              {recent.length === 0 ? (
                <div style={{ textAlign: 'center', color: 'var(--fg-3)', padding: 24 }}>
                  Uploads in this session will appear here.
                </div>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {recent.map(r => (
                    <li key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 'var(--r-1)', background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--fg-3)' }}>
                        <FileIcon size={16} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="hf-mono" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.fileName}</div>
                        <div className="hf-help" style={{ marginTop: 0 }}>
                          {formatSize(r.size)} · {formatAge(r.uploadedAt)} ·
                          {' '}{r.status === 'done' ? <CheckCircle2 size={11} style={{ verticalAlign: 'middle', color: 'var(--success)' }} /> : <AlertCircle size={11} style={{ verticalAlign: 'middle', color: 'var(--danger)' }} />}
                        </div>
                      </div>
                      <button
                        className="hf-btn ghost sm"
                        onClick={() => { navigator.clipboard?.writeText(r.url); }}
                        title="Copy filename"
                      >
                        <Copy size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FileUploadPage;
